import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Save } from "lucide-react";
import { toast } from "sonner";

import { Layout } from "@/components/Layout";
import { HoardexaLoader } from "@/components/HoardexaLoader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { usePopup } from "@/components/AppPopups";
import api, { formatApiError } from "@/lib/api";
import { fmtDate, inr } from "@/lib/format";
import { useHoardingTypes } from "@/lib/hoardingTypes";
import { useAuth } from "@/lib/auth";
import { hasAnyPermission, hasPermission } from "@/lib/permissions";
import { RegisterSearch } from "@/components/RegisterSearch";
import { PaginationFooter } from "@/components/PaginationFooter";
import {
  EMPTY,
  asArray,
  chargeFor,
  campaignContractTotal,
  defaultChargeForHoarding,
  latestBusinessMargins,
  marginProfit,
  updateCharge,
  validateCampaignMargins,
} from "./utils";

function todayInput() {
  return new Date().toISOString().slice(0, 10);
}

function toInputDate(value) {
  return (value || "").slice(0, 10);
}

function normalizeFormSnapshot(value) {
  const ids = asArray(value?.hoarding_ids).map(String).sort();
  const amounts = {};
  ids.forEach((id) => {
    const charge = chargeFor(value?.contract_amounts?.[id]);
    amounts[id] = {
      display: Number(charge.display || 0),
      printing: Number(charge.printing || 0),
      installation: Number(charge.installation || 0),
      display_margin_type: charge.display_margin_type || "",
      display_margin_value: Number(charge.display_margin_value || 0),
      printing_margin_type: charge.printing_margin_type || "",
      printing_margin_value: Number(charge.printing_margin_value || 0),
      installation_margin_type: charge.installation_margin_type || "",
      installation_margin_value: Number(charge.installation_margin_value || 0),
    };
  });

  return {
    name: value?.name || "",
    client_id: value?.client_id || null,
    start_date: value?.start_date || "",
    end_date: value?.is_open_ended ? "" : (value?.end_date || ""),
    is_open_ended: !!value?.is_open_ended,
    expected_end_date: value?.expected_end_date || "",
    end_date_status: value?.end_date_status || "confirmed",
    po_value_status: value?.po_value_status || "confirmed",
    confirmed_po_value: Number(value?.confirmed_po_value || 0),
    estimated_daily_charge: Number(value?.estimated_daily_charge || 0),
    estimated_monthly_charge: Number(value?.estimated_monthly_charge || 0),
    estimated_po_value: Number(value?.estimated_po_value || 0),
    payment_due_date: value?.payment_due_date || "",
    notes: value?.notes || "",
    gst: {
      gst_mode: value?.gst?.gst_mode || "exclusive",
      gst_percentage: Number(value?.gst?.gst_percentage || 0),
      cgst_percentage: Number(value?.gst?.cgst_percentage || 0),
      sgst_percentage: Number(value?.gst?.sgst_percentage || 0),
      igst_percentage: Number(value?.gst?.igst_percentage || 0),
      gst_treatment: value?.gst?.gst_treatment || "cgst_sgst",
      round_off_behavior: value?.gst?.round_off_behavior || "none",
    },
    hoarding_ids: ids,
    contract_amounts: amounts,
  };
}

function Field({ label, required, children, note }) {
  return (
    <label className="space-y-1">
      <span className="text-xs font-bold text-slate-600">
        {label} {required && <span className="text-blue-500">*</span>}
      </span>
      {children}
      {note && <p className="text-[11px] font-semibold text-slate-500">{note}</p>}
    </label>
  );
}

function Section({ title, children, right }) {
  return (
    <section className="hx-campaign-edit-section">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <h2 className="text-sm font-black text-slate-950">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
}

export function CampaignEdit() {
  const { id } = useParams();
  const location = useLocation();
  const nav = useNavigate();
  const popup = usePopup();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [hoardings, setHoardings] = useState([]);
  const [businessMargins, setBusinessMargins] = useState([]);
  const [accountingSettings, setAccountingSettings] = useState(null);
  const [hoardingSearch, setHoardingSearch] = useState("");
  const [committedHoardingSearch, setCommittedHoardingSearch] = useState("");
  const [hoardingFilters, setHoardingFilters] = useState({ city: "", type: "" });
  const [hoardingPage, setHoardingPage] = useState(1);
  const [hoardingPageSize, setHoardingPageSize] = useState(10);
  const [initialSelectedIds, setInitialSelectedIds] = useState([]);
  const loadedFormSnapshotRef = useRef("");
  const { names: hoardingTypes } = useHoardingTypes();

  const canAssignHoardings = hasPermission(user, "assign_hoardings_to_campaign");
  const canChangeDates = hasPermission(user, "change_campaign_dates");
  const canEditAmounts = hasAnyPermission(user, ["edit_campaign_amounts", "change_campaign_amounts"]);
  const canViewRevenue = hasAnyPermission(user, ["view_campaign_amounts", "view_basic_revenue", "view_profit_and_loss", "view_financial_reports"]);

  const originalStart = toInputDate(campaign?.start_date);
  const originalEnd = toInputDate(campaign?.end_date);
  const today = todayInput();
  const startDateLocked = originalStart && originalStart < today;
  const endDateLocked = originalEnd && originalEnd < today;
  const contractTotal = campaignContractTotal(form, hoardings);
  const hasUnsavedChanges = useMemo(() => {
    if (!loadedFormSnapshotRef.current) return false;
    return JSON.stringify(normalizeFormSnapshot(form)) !== loadedFormSnapshotRef.current;
  }, [form]);

  function hydrateForm(loaded, settings) {
    setCampaign(loaded);
    const selected = asArray(loaded?.hoarding_ids?.length ? loaded.hoarding_ids : loaded?.assigned_hoardings?.map((h) => h.id));
    const nextForm = {
      ...EMPTY,
      ...loaded,
      gst: {
        gst_mode: loaded?.gst_mode || settings?.default_gst_mode || "exclusive",
        gst_percentage: loaded?.gst_percentage || settings?.default_gst_percentage || 18,
        cgst_percentage: loaded?.cgst_percentage || settings?.default_cgst_percentage || 9,
        sgst_percentage: loaded?.sgst_percentage || settings?.default_sgst_percentage || 9,
        igst_percentage: loaded?.igst_percentage || settings?.default_igst_percentage || 18,
        gst_treatment: loaded?.gst_treatment || "cgst_sgst",
        round_off_behavior: settings?.default_round_off || "none",
      },
      hoarding_ids: selected,
      contract_amounts: loaded?.contract_amounts || {},
      start_date: toInputDate(loaded?.start_date),
      end_date: toInputDate(loaded?.end_date),
      expected_end_date: toInputDate(loaded?.expected_end_date),
      payment_due_date: toInputDate(loaded?.payment_due_date),
    };
    setForm(nextForm);
    loadedFormSnapshotRef.current = JSON.stringify(normalizeFormSnapshot(nextForm));
    setInitialSelectedIds(selected);
  }

  useEffect(() => {
    setLoading(true);
    const stateCampaign = location.state?.campaign?.id === id ? location.state.campaign : null;
    const campaignRequest = stateCampaign ? Promise.resolve({ data: stateCampaign }) : api.get(`/campaigns/${id}`, { skipGlobalLoader: true });
    Promise.all([
      campaignRequest,
      api.get("/campaigns/init", { skipGlobalLoader: true }),
      api.get("/accounts/settings", { skipGlobalLoader: true }).catch(() => ({ data: null })),
    ])
      .then(([campaignRes, initRes, settingsRes]) => {
        const init = initRes.data || {};
        setHoardings((Array.isArray(init.hoardings) ? init.hoardings : []).filter((h) =>
          h.occupancy_status !== "Blocked" || asArray(campaignRes.data?.hoarding_ids).includes(h.id)
        ));
        setBusinessMargins(Array.isArray(init.margins) ? init.margins : []);
        setAccountingSettings(settingsRes.data || null);
        hydrateForm(campaignRes.data || null, settingsRes.data || null);
      })
      .catch((err) => {
        toast.error(formatApiError(err.response?.data?.detail) || "Campaign could not be loaded");
        setCampaign(null);
      })
      .finally(() => setLoading(false));
  }, [id, location.state]);

  const pickableHoardings = useMemo(() => {
    const term = committedHoardingSearch.trim().toLowerCase();
    const filtered = hoardings
      .filter((h) => !hoardingFilters.city || h.city === hoardingFilters.city)
      .filter((h) => !hoardingFilters.type || h.type === hoardingFilters.type)
      .filter((h) => {
        if (!term) return true;
        return h.name?.toLowerCase().includes(term) || h.hoarding_id?.toLowerCase().includes(term);
      });

    // Sort initially selected hoardings to the top
    return [...filtered].sort((a, b) => {
      const aSel = initialSelectedIds.includes(a.id);
      const bSel = initialSelectedIds.includes(b.id);
      if (aSel && !bSel) return -1;
      if (!aSel && bSel) return 1;
      return 0;
    });
  }, [hoardings, hoardingFilters.city, hoardingFilters.type, committedHoardingSearch, initialSelectedIds]);

  const totalDisplayPages = Math.max(1, Math.ceil(pickableHoardings.length / hoardingPageSize));
  const pageItems = useMemo(() => {
    return pickableHoardings.slice((hoardingPage - 1) * hoardingPageSize, hoardingPage * hoardingPageSize);
  }, [pickableHoardings, hoardingPage, hoardingPageSize]);

  const cities = useMemo(() => {
    return [...new Set(hoardings.map((h) => h.city).filter(Boolean))].sort();
  }, [hoardings]);
  const gstTreatment = form.gst?.gst_treatment || "cgst_sgst";

  function toggleHoarding(hoardingID, checked) {
    const hoarding = hoardings.find((item) => item.id === hoardingID);
    if (checked && hoarding?.occupancy_status === "Blocked") {
      toast.error("Blocked hoardings cannot be added to campaigns");
      return;
    }
    setForm((prev) => {
      const ids = checked ? [...prev.hoarding_ids, hoardingID] : prev.hoarding_ids.filter((item) => item !== hoardingID);
      const amounts = { ...prev.contract_amounts };
      if (checked) {
        const hoarding = hoardings.find((item) => item.id === hoardingID);
        amounts[hoardingID] = defaultChargeForHoarding(hoarding, latestBusinessMargins(businessMargins, prev.start_date), amounts[hoardingID]);
      } else {
        delete amounts[hoardingID];
      }
      return { ...prev, hoarding_ids: ids, contract_amounts: amounts };
    });
  }

  function updateHoardingCharge(hoardingID, field, value) {
    setForm((prev) => ({ ...prev, contract_amounts: updateCharge(prev.contract_amounts, hoardingID, field, value) }));
  }

  function updateGst(field, value) {
    setForm((prev) => {
      const gst = { ...(prev.gst || {}) };
      if (field === "gst_percentage") {
        const pct = Number(value || 0);
        return {
          ...prev,
          gst: {
            ...gst,
            gst_percentage: value,
            ...(gst.gst_treatment === "igst"
              ? { igst_percentage: value }
              : { cgst_percentage: pct / 2, sgst_percentage: pct / 2 }),
          },
        };
      }
      if (field === "gst_treatment") {
        const pct = Number(gst.gst_percentage || accountingSettings?.default_gst_percentage || 18);
        return {
          ...prev,
          gst: {
            ...gst,
            gst_treatment: value,
            ...(value === "igst"
              ? { igst_percentage: gst.igst_percentage || pct }
              : { cgst_percentage: gst.cgst_percentage || pct / 2, sgst_percentage: gst.sgst_percentage || pct / 2 }),
          },
        };
      }
      if (field === "igst_percentage") return { ...prev, gst: { ...gst, igst_percentage: value, gst_percentage: value } };
      if (field === "cgst_percentage" || field === "sgst_percentage") {
        const next = { ...gst, [field]: value };
        next.gst_percentage = Number(next.cgst_percentage || 0) + Number(next.sgst_percentage || 0);
        return { ...prev, gst: next };
      }
      return { ...prev, gst: { ...gst, [field]: value } };
    });
  }

  function validateBeforeSave() {
    if (!form.name.trim()) return "Campaign name is required";
    if (!form.client_id) return "Client is required";
    if (!form.start_date) return "Start date is required";
    if (startDateLocked && form.start_date !== originalStart) return "This campaign already started. Start date cannot be changed.";
    if (!form.is_open_ended && !form.end_date) return "End date is required unless this is open-ended.";
    if (endDateLocked && form.end_date !== originalEnd) return "This campaign already ended. End date cannot be changed.";
    if (!endDateLocked && form.end_date && form.end_date < today) return "End date cannot be moved to a previous date.";
    if (!form.is_open_ended && new Date(form.end_date) <= new Date(form.start_date)) return "End date must be after start date";
    if (form.hoarding_ids.length === 0) return "Select at least one hoarding";
    const marginError = validateCampaignMargins(form);
    if (marginError) return marginError;
    return "";
  }

  async function save({ navigateBack = false } = {}) {
    const validationError = validateBeforeSave();
    if (validationError) {
      toast.error(validationError);
      return false;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        end_date: form.is_open_ended ? null : form.end_date,
        expected_end_date: form.expected_end_date || null,
        payment_due_date: form.payment_due_date || null,
        end_date_status: form.is_open_ended ? (form.end_date_status || "unknown") : (form.end_date_status || "confirmed"),
        po_value_status: form.po_value_status || "confirmed",
        estimated_daily_charge: Number(form.estimated_daily_charge || 0),
        estimated_monthly_charge: Number(form.estimated_monthly_charge || 0),
        estimated_po_value: Number(form.estimated_po_value || 0),
        confirmed_po_value: Number(contractTotal || 0),
        gst: {
          ...(form.gst || {}),
          gst_percentage: Number(form.gst?.gst_percentage || 0),
          cgst_percentage: Number(form.gst?.cgst_percentage || 0),
          sgst_percentage: Number(form.gst?.sgst_percentage || 0),
          igst_percentage: Number(form.gst?.igst_percentage || 0),
        },
      };
      if (!canAssignHoardings) delete payload.hoarding_ids;
      if (!canChangeDates) {
        delete payload.start_date;
        delete payload.end_date;
        delete payload.is_open_ended;
        delete payload.expected_end_date;
        delete payload.end_date_status;
      }
      if (!canEditAmounts) delete payload.contract_amounts;
      await api.put(`/campaigns/${id}`, payload);
      toast.success("Campaign updated");
      loadedFormSnapshotRef.current = JSON.stringify(normalizeFormSnapshot(form));
      if (navigateBack) nav(`/campaigns/${id}`);
      return true;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Campaign could not be updated");
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function handleBack() {
    if (!hasUnsavedChanges) {
      nav(`/campaigns/${id}`);
      return;
    }
    const shouldUpdate = await popup.confirm({
      title: "Update campaign before going back?",
      description: "You are leaving the edit page. Choose Update to save the current changes, or Cancel to return without saving.",
      confirmText: "Update",
      cancelText: "Cancel",
      tone: "indigo",
    });
    if (shouldUpdate) {
      await save({ navigateBack: true });
    } else {
      nav(`/campaigns/${id}`);
    }
  }

  const actions = (
    <div className="flex items-center gap-2">
      <Button variant="outline" onClick={handleBack}><ChevronLeft size={15} /> Back</Button>
      <Button onClick={() => save()} disabled={saving}><Save size={15} /> {saving ? "Saving..." : "Update"}</Button>
    </div>
  );

  return (
    <Layout title="Edit campaign" subtitle={campaign?.campaign_id || "Campaign workspace"} actions={actions}>
      {loading ? (
        <HoardexaLoader label="Loading campaign editor" />
      ) : !campaign ? (
        <Section title="Campaign not found">
          <p className="text-sm text-slate-500">The campaign could not be loaded or is outside your access scope.</p>
        </Section>
      ) : (
        <div className="hx-hoardings-page hx-campaign-edit-page max-w-6xl">
          <Section title="Campaign basics">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Campaign name" required><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
              <Field label="Payment due date"><Input type="date" value={form.payment_due_date || ""} onChange={(e) => setForm({ ...form, payment_due_date: e.target.value })} /></Field>
            </div>
          </Section>

          <Section title="Dates">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field label="Start date" required note={startDateLocked ? `Locked because original start was ${fmtDate(originalStart)}.` : "Can be changed until the campaign start date has passed."}>
                <Input type="date" value={form.start_date || ""} disabled={!canChangeDates || startDateLocked} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
              </Field>
              <Field label="End date" required={!form.is_open_ended} note={endDateLocked ? `Locked because original end was ${fmtDate(originalEnd)}.` : "Can only be set to today or a future date."}>
                <Input type="date" min={!endDateLocked ? today : undefined} value={form.end_date || ""} disabled={form.is_open_ended || !canChangeDates || endDateLocked} onChange={(e) => setForm({ ...form, end_date: e.target.value, end_date_status: "confirmed" })} />
              </Field>
              <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 sm:col-span-2">
                <Checkbox checked={!!form.is_open_ended} disabled={!canChangeDates || endDateLocked} onCheckedChange={(checked) => setForm({ ...form, is_open_ended: !!checked, end_date: checked ? "" : form.end_date, end_date_status: checked ? "unknown" : "confirmed" })} />
                End date not known yet
              </label>
            </div>
          </Section>

          {canViewRevenue && (
            <Section
              title="PO and breakup control"
              right={<span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-700">Breakup {inr(contractTotal)}</span>}
            >
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="PO status">
                  <Select value={form.po_value_status || "confirmed"} onValueChange={(v) => setForm({ ...form, po_value_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="confirmed">Confirmed</SelectItem><SelectItem value="estimated">Estimated</SelectItem><SelectItem value="unknown">Unknown</SelectItem></SelectContent>
                  </Select>
                </Field>
                <Field label="Confirmed PO value">
                  <div className="flex h-10 items-center rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-bold text-slate-900">
                    {inr(contractTotal)}
                  </div>
                </Field>
              </div>
            </Section>
          )}

          {canViewRevenue && (
            <Section title="GST & Taxation Settings">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <Field label="GST Mode">
                  <Select value={form.gst?.gst_mode || "exclusive"} onValueChange={(v) => updateGst("gst_mode", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="exclusive">Exclusive</SelectItem>
                      <SelectItem value="inclusive">Inclusive</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="GST %">
                  <Input type="number" min="0" value={form.gst?.gst_percentage ?? accountingSettings?.default_gst_percentage ?? 18} onChange={(e) => updateGst("gst_percentage", e.target.value)} />
                </Field>
                <Field label="Split/Treatment">
                  <Select value={gstTreatment} onValueChange={(v) => updateGst("gst_treatment", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cgst_sgst">CGST + SGST</SelectItem>
                      <SelectItem value="igst">IGST</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                {gstTreatment === "igst" ? (
                  <Field label="IGST %">
                    <Input type="number" min="0" value={form.gst?.igst_percentage ?? accountingSettings?.default_igst_percentage ?? 18} onChange={(e) => updateGst("igst_percentage", e.target.value)} />
                  </Field>
                ) : (
                  <>
                    <Field label="CGST %">
                      <Input type="number" min="0" value={form.gst?.cgst_percentage ?? accountingSettings?.default_cgst_percentage ?? 9} onChange={(e) => updateGst("cgst_percentage", e.target.value)} />
                    </Field>
                    <Field label="SGST %">
                      <Input type="number" min="0" value={form.gst?.sgst_percentage ?? accountingSettings?.default_sgst_percentage ?? 9} onChange={(e) => updateGst("sgst_percentage", e.target.value)} />
                    </Field>
                  </>
                )}
                <Field label="Round Off">
                  <Select value={form.gst?.round_off_behavior || "none"} onValueChange={(v) => updateGst("round_off_behavior", v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="nearest_rupee">Nearest rupee</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              </div>
            </Section>
          )}

          <Section title={`Hoarding breakup (${form.hoarding_ids.length} selected)`}>
            <div className="hx-inventory-register">
              <header>
                <div>
                  <h2>Assign Hoardings</h2>
                </div>
                <div className="hx-register-controls">
                  <RegisterSearch
                    value={hoardingSearch}
                    onChange={setHoardingSearch}
                    onSearch={() => {
                      setCommittedHoardingSearch(hoardingSearch.trim());
                      setHoardingPage(1);
                    }}
                    placeholder="Search Hoardings..."
                    testId="campaign-hoardings-search"
                    ariaLabel="Search hoardings"
                  />
                  <Select value={String(hoardingPageSize)} onValueChange={(v) => { setHoardingPageSize(Number(v)); setHoardingPage(1); }}>
                    <SelectTrigger className="hx-register-rows">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10 per page</SelectItem>
                      <SelectItem value="25">25 per page</SelectItem>
                      <SelectItem value="50">50 per page</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </header>

              <div className="hx-register-filterbar">
                <label className="hx-register-filter">
                  <span>City</span>
                  <Select value={hoardingFilters.city || "all"} onValueChange={(v) => { setHoardingFilters({ ...hoardingFilters, city: v === "all" ? "" : v }); setHoardingPage(1); }}>
                    <SelectTrigger><SelectValue placeholder="All Cities" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Cities</SelectItem>
                      {cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </label>

                <label className="hx-register-filter">
                  <span>Type</span>
                  <Select value={hoardingFilters.type || "all"} onValueChange={(v) => { setHoardingFilters({ ...hoardingFilters, type: v === "all" ? "" : v }); setHoardingPage(1); }}>
                    <SelectTrigger><SelectValue placeholder="All Types" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {hoardingTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      {hoardingTypes.length === 0 && <SelectItem value="__no_types" disabled>No types yet</SelectItem>}
                    </SelectContent>
                  </Select>
                </label>
              </div>

              {/* Mobile View */}
              <div className="hx-mobile-hoarding-list md:hidden">
                {pageItems.map((hoarding) => {
                  const checked = form.hoarding_ids.includes(hoarding.id);
                  const charge = chargeFor(form.contract_amounts?.[hoarding.id]);
                  const printingProfit = marginProfit(charge.printing, charge.printing_margin_type, charge.printing_margin_value);
                  const installationProfit = marginProfit(charge.installation, charge.installation_margin_type, charge.installation_margin_value);
                  return (
                    <div key={hoarding.id} className={`hx-mobile-hoarding-card relative ${checked ? "border-blue-200 bg-blue-50/50" : ""}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox checked={checked} disabled={!canAssignHoardings} onCheckedChange={(value) => toggleHoarding(hoarding.id, value)} className="mt-1" />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <div className="min-w-0">
                              <h3 className="text-sm font-semibold text-slate-950 truncate" title={hoarding.name}>{hoarding.name}</h3>
                              <p className="mt-0.5 text-xs text-slate-500 font-mono">{hoarding.hoarding_id}</p>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-2 text-[10px] font-semibold text-slate-600">
                            {hoarding.type && <span className="bg-slate-100 px-2 py-0.5 rounded-full">{hoarding.type}</span>}
                            {hoarding.city && <span className="bg-slate-100 px-2 py-0.5 rounded-full">{hoarding.city}</span>}
                            {hoarding.width && hoarding.height && <span className="bg-slate-100 px-2 py-0.5 rounded-full">{hoarding.width} x {hoarding.height}</span>}
                          </div>
                        </div>
                      </div>
                      {checked && canViewRevenue && (
                        <div className="mt-3 border-t border-slate-200/60 pt-3 grid grid-cols-1 gap-2">
                          <Field label="Display"><Input type="number" min="0" value={charge.display || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "display", e.target.value)} className="h-8 text-xs bg-white" /></Field>
                          <Field label={`Printing profit ${inr(printingProfit)}`}><Input type="number" min="0" value={charge.printing || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "printing", e.target.value)} className="h-8 text-xs bg-white" /></Field>
                          <Field label={`Mounting profit ${inr(installationProfit)}`}><Input type="number" min="0" value={charge.installation || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "installation", e.target.value)} className="h-8 text-xs bg-white" /></Field>
                        </div>
                      )}
                    </div>
                  );
                })}
                {pageItems.length === 0 && (
                  <div className="py-8 text-center text-xs font-semibold text-slate-500">No hoardings found.</div>
                )}
              </div>

              {/* Desktop View */}
              <div className="hx-inventory-table-wrap hidden md:block">
                <table className="hx-campaign-edit-hoarding-table">
                  <thead>
                    <tr>
                      <th className="is-select text-center">Select</th>
                      <th className="is-hoarding">Hoarding</th>
                      <th>Type</th>
                      <th>City</th>
                      <th>Size</th>
                      <th className="text-right">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageItems.map((hoarding) => {
                      const checked = form.hoarding_ids.includes(hoarding.id);
                      const charge = chargeFor(form.contract_amounts?.[hoarding.id]);
                      const printingProfit = marginProfit(charge.printing, charge.printing_margin_type, charge.printing_margin_value);
                      const installationProfit = marginProfit(charge.installation, charge.installation_margin_type, charge.installation_margin_value);
                      return (
                        <Fragment key={hoarding.id}>
                          <tr className={checked ? "is-selected" : ""}>
                            <td className="text-center">
                              <Checkbox checked={checked} disabled={!canAssignHoardings} onCheckedChange={(value) => toggleHoarding(hoarding.id, value)} />
                            </td>
                            <td>
                              <div className="hx-hoarding-cell">
                                <div className="min-w-0">
                                  <strong title={hoarding.name}>{hoarding.name}</strong>
                                  <span><b>{hoarding.hoarding_id}</b></span>
                                </div>
                              </div>
                            </td>
                            <td className="font-medium text-slate-700">{hoarding.type || "—"}</td>
                            <td className="font-medium text-slate-700">{hoarding.city || "—"}</td>
                            <td><span className="hx-size-cell">{hoarding.width && hoarding.height ? `${hoarding.width} x ${hoarding.height}` : "—"}</span></td>
                            <td className="text-right hx-price-cell">{hoarding.monthly_price ? inr(hoarding.monthly_price) : "—"}</td>
                          </tr>
                          {checked && canViewRevenue && (
                            <tr className="hx-campaign-charge-row">
                              <td />
                              <td colSpan={5}>
                                <div className="hx-campaign-charge-grid">
                                  <Field label="Display"><Input type="number" min="0" value={charge.display || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "display", e.target.value)} /></Field>
                                  <Field label={`Printing profit ${inr(printingProfit)}`}><Input type="number" min="0" value={charge.printing || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "printing", e.target.value)} /></Field>
                                  <Field label={`Mounting profit ${inr(installationProfit)}`}><Input type="number" min="0" value={charge.installation || ""} disabled={!canEditAmounts} onChange={(e) => updateHoardingCharge(hoarding.id, "installation", e.target.value)} /></Field>
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                    {pageItems.length === 0 && (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-slate-500 font-semibold">No hoardings found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {totalDisplayPages > 1 && (
                <div className="border-t border-slate-100 p-3">
                  <PaginationFooter page={hoardingPage} pageSize={hoardingPageSize} total={pickableHoardings.length} onPageChange={setHoardingPage} />
                </div>
              )}
            </div>
          </Section>

          <Section title="Notes">
            <Textarea rows={3} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </Section>
        </div>
      )}
    </Layout>
  );
}
